import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Coffee, ForkKnife, Moon, Users } from "@phosphor-icons/react";

/**
 * Verpflegung heute — Liste aller heutigen Gäste mit ihren Essenswünschen.
 * Datenquelle: GET /api/meals/today
 */
export default function MealsTodayDialog({ open, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/meals/today").then(r => setData(r.data)).catch(() => setData({ rows: [], totals: {} }));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v)=>{ if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="meals-today-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coffee size={22} weight="duotone" className="text-primary"/>
            Verpflegung heute
            {data && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                · {new Date(data.date).toLocaleDateString("de-DE",{day:"2-digit",month:"long",year:"numeric"})}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {!data && <div className="py-6 text-sm text-muted-foreground">Lade …</div>}

        {data && (
          <>
            <div className="grid grid-cols-3 gap-2 py-2">
              <StatChip icon={Coffee}    color="text-orange-600"  label="Frühstück"   value={data.totals.breakfast || 0}/>
              <StatChip icon={ForkKnife} color="text-amber-600"   label="Mittagessen" value={data.totals.lunch || 0}/>
              <StatChip icon={Moon}      color="text-indigo-600"  label="Abendessen"  value={data.totals.dinner || 0}/>
            </div>

            {data.rows.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Heute keine Gäste im Haus.
              </div>
            ) : (
              <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                {data.rows.map(row => (
                  <div key={row.id}
                       className="rounded-2xl border border-border bg-white p-3.5 flex items-center gap-3 flex-wrap"
                       data-testid={`meals-row-${row.id}`}>
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-baseline gap-2">
                        <span className="inline-flex items-center gap-1 rounded-lg bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest">
                          {row.room_name}
                        </span>
                        <span className="font-semibold text-base">{row.guest_name}</span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground inline-flex items-center gap-1">
                        <Users size={12} weight="bold"/> {row.persons} {row.persons===1?"Person":"Personen"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <MealBadge icon={Coffee}    label="F" value={row.breakfast} active={row.breakfast > 0} tone="orange"/>
                      <MealBadge icon={ForkKnife} label="M" value={row.lunch}     active={row.lunch > 0}     tone="amber"/>
                      <MealBadge icon={Moon}      label="A" value={row.dinner}    active={row.dinner > 0}    tone="indigo"/>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <p className="text-[11px] text-muted-foreground mt-2">
          F = Frühstück · M = Mittagessen · A = Abendessen · Portionen pro Gast
        </p>
      </DialogContent>
    </Dialog>
  );
}

function StatChip({ icon: Icon, color, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-muted/30 p-3 flex items-center gap-2">
      <Icon size={22} weight="duotone" className={color}/>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
        <div className="text-xl font-bold leading-none mt-0.5" style={{fontFamily:"'Work Sans',sans-serif"}}>{value}</div>
      </div>
    </div>
  );
}

function MealBadge({ icon: Icon, label, value, active, tone }) {
  const cls = active
    ? {
        orange: "bg-orange-100 text-orange-800 border-orange-200",
        amber:  "bg-amber-100 text-amber-800 border-amber-200",
        indigo: "bg-indigo-100 text-indigo-800 border-indigo-200",
      }[tone]
    : "bg-muted/30 text-muted-foreground border-border";
  return (
    <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold border ${cls}`}
          title={label}>
      <Icon size={13} weight={active ? "fill" : "regular"}/>
      {value > 0 ? `×${value}` : "–"}
    </span>
  );
}
